/** An item is a social media post managed through the Publisher API. An item can be a published post, a scheduled post, or a draft, as indicated by its `status`, and it targets a single connected social channel such as Instagram, Facebook, or TikTok. */
export interface Item extends ItemDataOneOf {
  /** Instagram post content. */
  instagramPost?: InstagramPost;
  /** Instagram story content. */
  instagramStory?: InstagramStory;
  /** Facebook post content. */
  facebookPost?: FacebookPost;
  /** Facebook reel content. */
  facebookReel?: FacebookReel;
  /** Facebook story content. */
  facebookStory?: FacebookStory;
  /** YouTube video content. */
  youtubeVideo?: YouTubeVideo;
  /** YouTube short content. */
  youtubeShort?: YouTubeShort;
  /** LinkedIn post content. */
  linkedinPost?: LinkedinPost;
  /**
   * Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @deprecated Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @targetRemovalDate 2026-07-31
   */
  twitterPost?: TwitterPost;
  /** Pinterest post content. */
  pinterestPost?: PinterestPost;
  /** Google Business Profile post content. */
  gbpPost?: GbpPost;
  /** TikTok video content. */
  tiktokVideo?: TikTokVideo;
  /** TikTok photo content. */
  tiktokPhoto?: TikTokPhoto;
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /** Social channel the item is published to, and the account within that channel. */
  channel?: Channel;
  /**
   * Item format. The `type` and the content set in `data` must match a combination supported by the item's `channel.name`:
   *
   * - Instagram: `POST` with `instagramPost`, or `STORY` with `instagramStory`.
   * - Facebook: `POST` with `facebookPost`, `REEL` with `facebookReel`, or `STORY` with `facebookStory`.
   * - YouTube: `VIDEO` with `youtubeVideo`, or `SHORT` with `youtubeShort`.
   * - LinkedIn: `POST` with `linkedinPost`.
   * - X (Twitter): `POST` with `twitterPost`. Deprecated: No longer functional as of July 31, 2026.
   * - Pinterest: `POST` with `pinterestPost`.
   * - Google Business Profile: `POST` with `gbpPost`.
   * - TikTok: `POST` with `tiktokPhoto`, or `VIDEO` with `tiktokVideo`.
   */
  type?: ItemTypeWithLiterals;
  /**
   * Current status of the item in its lifecycle.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Date and time the item was published. Returned only after the item is published.
   * @readonly
   */
  publishedDate?: Date | null;
  /** Scheduling info. Set to schedule the item for a future date instead of publishing it immediately. */
  schedulingInfo?: SchedulingInfo;
  /** Identifies what created the item. */
  consumerInfo?: ConsumerInfo;
  /** Caller-defined reference that uniquely identifies the item. Can be used to prevent duplicate publishing: an attempt to publish a second item with the same `referenceId` fails. Can also be used as a UTM campaign ID. */
  referenceId?: string | null;
  /** Aggregated analytics for the site, attributed to traffic from this post. */
  siteAnalytics?: SiteAnalytics;
  /** Summary of the post's performance on the social channel, such as impressions and interactions. */
  insightsSummary?: InsightsSummary;
  /** Creation and update dates, returned for draft items. */
  draftInfo?: DraftInfo;
  /** Whether the item was generated as an AI suggestion. */
  suggested?: boolean;
  /** Whether the item is a post created natively on the social channel rather than published through Wix. */
  external?: boolean;
  /**
   * Details of why the item failed to publish. Returned only when `status` is `FAILED`.
   * @readonly
   */
  publicationError?: PublicationErrorDetails;
  /**
   * Reference to the site content the post promotes, such as a product, a blog post, or an event.
   * Set per channel.
   */
  siteAssetRef?: AssetReference;
  /**
   * ID of the item this item was derived from, when publishing produced several items
   * from one. Currently set only by `PublishMultiStoryDraft`, which splits a multi-media
   * story draft into one published item per frame: every resulting item carries the
   * originating draft's ID here. Lets a consumer re-correlate the new items with whatever
   * it had recorded against the original draft, which no longer exists after the split.
   * Set by the publisher, never by the caller.
   * @format GUID
   * @readonly
   */
  originItemId?: string | null;
  /**
   * Engagement automations on the published post. Updates replace the whole list. Facebook posts only.
   * @internal
   */
  automationsWrapper?: ItemAutomationsWrapper;
}

/** @oneof */
export interface ItemDataOneOf {
  /** Instagram post content. */
  instagramPost?: InstagramPost;
  /** Instagram story content. */
  instagramStory?: InstagramStory;
  /** Facebook post content. */
  facebookPost?: FacebookPost;
  /** Facebook reel content. */
  facebookReel?: FacebookReel;
  /** Facebook story content. */
  facebookStory?: FacebookStory;
  /** YouTube video content. */
  youtubeVideo?: YouTubeVideo;
  /** YouTube short content. */
  youtubeShort?: YouTubeShort;
  /** LinkedIn post content. */
  linkedinPost?: LinkedinPost;
  /**
   * Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @deprecated Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @targetRemovalDate 2026-07-31
   */
  twitterPost?: TwitterPost;
  /** Pinterest post content. */
  pinterestPost?: PinterestPost;
  /** Google Business Profile post content. */
  gbpPost?: GbpPost;
  /** TikTok video content. */
  tiktokVideo?: TikTokVideo;
  /** TikTok photo content. */
  tiktokPhoto?: TikTokPhoto;
}

/** Supported social channel. */
export enum ChannelName {
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK = 'FACEBOOK',
  YOUTUBE = 'YOUTUBE',
  LINKEDIN = 'LINKEDIN',
  /** Deprecated: No longer functional as of July 31, 2026. */
  TWITTER = 'TWITTER',
  PINTEREST = 'PINTEREST',
  /** Google Business Profile. */
  GBP = 'GBP',
  TIKTOK = 'TIKTOK',
}

/** @enumType */
export type ChannelNameWithLiterals =
  | ChannelName
  | 'INSTAGRAM'
  | 'FACEBOOK'
  | 'YOUTUBE'
  | 'LINKEDIN'
  | 'TWITTER'
  | 'PINTEREST'
  | 'GBP'
  | 'TIKTOK';

export interface PublishingTarget {
  /**
   * ID of the publishing target.
   * @maxLength 128
   */
  id?: string | null;
  /**
   * Display name of the publishing target.
   * @maxLength 2000
   */
  displayName?: string | null;
  /**
   * URL of the publishing target's picture.
   * @format WEB_URL
   */
  pictureUrl?: string | null;
}

export enum ConsumerType {
  METASITE = 'METASITE',
}

/** @enumType */
export type ConsumerTypeWithLiterals = ConsumerType | 'METASITE';

/** Type of site asset. */
export enum Type {
  /** A Wix Blog post. */
  BLOG_POST = 'BLOG_POST',
  /** A Wix Events event. */
  EVENT = 'EVENT',
  /** A Wix Stores product. */
  STORES_PRODUCT = 'STORES_PRODUCT',
  /** A Wix Bookings service. */
  BOOKINGS_SERVICE = 'BOOKINGS_SERVICE',
  /** A Wix Stores coupon. */
  STORES_COUPON = 'STORES_COUPON',
  /** A Wix Stores category. */
  STORES_CATEGORY = 'STORES_CATEGORY',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'BLOG_POST'
  | 'EVENT'
  | 'STORES_PRODUCT'
  | 'BOOKINGS_SERVICE'
  | 'STORES_COUPON'
  | 'STORES_CATEGORY';

export interface ItemAutomation
  extends ItemAutomationTriggerOneOf,
    ItemAutomationActionOneOf {
  /** Triggered by a comment on the post. */
  commentTrigger?: CommentTrigger;
  /** Sends the commenter an automated private reply. */
  sendPrivateReply?: SendPrivateReplyAction;
  /** Whether the automation is active. */
  enabled?: boolean;
}

/** @oneof */
export interface ItemAutomationTriggerOneOf {
  /** Triggered by a comment on the post. */
  commentTrigger?: CommentTrigger;
}

/** @oneof */
export interface ItemAutomationActionOneOf {
  /** Sends the commenter an automated private reply. */
  sendPrivateReply?: SendPrivateReplyAction;
}

export interface CommentTrigger {
  /**
   * The automation fires when a comment contains any of these phrases (case-insensitive).
   * @maxSize 20
   * @maxLength 200
   */
  phrases?: string[];
}

export interface SendPrivateReplyAction {
  /**
   * Message text of the private reply.
   * @maxLength 2000
   */
  message?: string | null;
}

export interface Channel {
  /** Social channel the item is published to. */
  name?: ChannelNameWithLiterals;
  /** ID of the connected account the item is published to. */
  accountId?: string | null;
  /**
   * Display name of the connected account the item is published to.
   * @readonly
   */
  accountName?: string | null;
  /**
   * ID of the post on the social channel. Returned after the item is published.
   * @readonly
   */
  externalItemId?: string | null;
  /**
   * URL of the post on the social channel. Returned after the item is published.
   * @readonly
   * @format WEB_URL
   */
  externalItemUrl?: string | null;
  /** Specific destination within the account, such as a Facebook page or a Pinterest board. */
  publishingTarget?: PublishingTarget;
}

/** Item format. */
export enum ItemType {
  /** A standard post. */
  POST = 'POST',
  /** A short-form vertical video reel. */
  REEL = 'REEL',
  /** A video. */
  VIDEO = 'VIDEO',
  /** A short-form vertical video. */
  SHORT = 'SHORT',
  /** A story that's available for a limited time. */
  STORY = 'STORY',
}

/** @enumType */
export type ItemTypeWithLiterals =
  | ItemType
  | 'POST'
  | 'REEL'
  | 'VIDEO'
  | 'SHORT'
  | 'STORY';

/** Status of an item in its lifecycle. */
export enum Status {
  /** Published to the social channel. */
  PUBLISHED = 'PUBLISHED',
  /** Scheduled to be published at a future date. */
  SCHEDULED = 'SCHEDULED',
  /** Scheduling was canceled before the item was published. */
  CANCELED = 'CANCELED',
  /** Publishing failed. */
  FAILED = 'FAILED',
  /** Deleted. */
  DELETED = 'DELETED',
  /** Publishing is in progress. */
  PROCESSING = 'PROCESSING',
  /** Saved as a draft, not yet published. */
  DRAFT = 'DRAFT',
  /** Queued for publishing as part of a bulk operation. */
  IN_QUEUE = 'IN_QUEUE',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'PUBLISHED'
  | 'SCHEDULED'
  | 'CANCELED'
  | 'FAILED'
  | 'DELETED'
  | 'PROCESSING'
  | 'DRAFT'
  | 'IN_QUEUE';

export interface SchedulingInfo {
  /** Date and time the item is scheduled to be published. */
  scheduledDate?: Date | null;
  /**
   * Date and time the item was scheduled.
   * @readonly
   */
  schedulingTimestamp?: Date | null;
}

export interface ConsumerInfo {
  /**
   * The consumer type
   * @internal
   */
  type?: ConsumerTypeWithLiterals;
  /**
   * The consumer id
   * @internal
   */
  id?: string | null;
  /**
   * Stable name identifying what created the item, for example `social-marketing` for
   * the Social Media Marketing dashboard. Set on creation only. Update Item ignores it.
   */
  name?: string | null;
  /**
   * The consumer asset id e.g. store item, an event etc'
   * @internal
   */
  assetId?: string | null;
}

export interface InstagramPost extends InstagramPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Caption for the post. Can include hashtags (for example, `#crazywildebeest`) and mentions of Instagram users (for example, `@natgeo`). Mentioned users are notified when the post is published. Supports up to 30 hashtags and 20 mentions.
   * @maxLength 2200
   */
  caption?: string | null;
  /**
   * The post URL on Instagram.
   * @readonly
   */
  postUrl?: string | null;
  /**
   * Insights from Instagram.
   * @readonly
   */
  insights?: InstagramInsights;
}

/** @oneof */
export interface InstagramPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface MediaWrapper {
  /** List of media items. */
  media?: Media[];
}

export interface Media {
  /** Media type. */
  type?: MediaTypeWithLiterals;
  /**
   * Media URL.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Media Manager file ID backing the media. Available only when the media is stored in the Media Manager.
   * @maxLength 1000
   */
  fileId?: string | null;
  /**
   * Set on media whose url is temporary and should be imported into the user's Media Manager.
   * May be passed on input, and may also be returned when relevant so the client can pass it back
   * on the item write. When true, the server imports the temporary url into the user's Media Manager
   * on publish/schedule/save and stores the resulting url + file_id.
   * @internal
   */
  requiresPersistence?: boolean | null;
}

export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

/** @enumType */
export type MediaTypeWithLiterals = MediaType | 'IMAGE' | 'VIDEO';

export interface InstagramInsights {
  /** Number of likes. */
  likes?: number;
  /** Number of engagements. */
  engagement?: number;
  /** Number of impressions. */
  impressions?: number;
  /** Number of plays. */
  plays?: number;
  /** Number of accounts reached. */
  reach?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of shares. */
  shares?: number;
  /** Number of saves. */
  saved?: number;
  /** Total number of interactions. */
  totalInteractions?: number;
}

export interface InstagramStory {
  /**
   * The story URL on Instagram.
   * @readonly
   * @format WEB_URL
   */
  storyUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * insights data from Instagram.
   * @readonly
   */
  insights?: InstagramStoryInsights;
}

export interface InstagramStoryInsights {
  /** Number of impressions. */
  impressions?: number;
  /** Number of accounts reached. */
  reach?: number;
  /** Number of replies. */
  replies?: number;
}

export interface FacebookPost extends FacebookPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /** The page ID to publish the post to. */
  pageId?: string | null;
  /** Caption for the post. */
  caption?: string | null;
  /**
   * The post URL on Facebook.
   * @readonly
   */
  postUrl?: string | null;
  /** Link preview metadata. Available only when the post contains a link. */
  linkMetadata?: LinkMetadata;
  /**
   * Insights from Facebook.
   * @readonly
   */
  insights?: FacebookInsights;
}

/** @oneof */
export interface FacebookPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface LinkMetadata {
  /** Thumbnail URL for the link preview. */
  thumbnailUrl?: string | null;
  /** Title for the link preview. */
  title?: string | null;
  /** Description for the link preview. */
  description?: string | null;
}

export interface FacebookInsights {
  /** Impression counts. */
  impressions?: FacebookImpressions;
  /** Interaction counts. */
  interactions?: FacebookInteractions;
  /** Reaction counts. */
  reactions?: FacebookReactions;
  /** Activity counts. */
  activity?: FacebokActivity;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface FacebookImpressions {
  /** Number of unique impressions. */
  unique?: number;
  /** Total number of impressions. */
  total?: number;
}

export interface FacebookInteractions {
  /** Number of unique interactions. */
  unique?: number;
  /** Total number of interactions. */
  total?: number;
}

export interface FacebookReactions {
  /** Number of "like" reactions. */
  like?: number;
  /** Number of "love" reactions. */
  love?: number;
  /** Number of "wow" reactions. */
  wow?: number;
  /** Number of "haha" reactions. */
  haha?: number;
  /** Number of "sad" reactions. */
  sad?: number;
  /** Number of "angry" reactions. */
  anger?: number;
}

export interface FacebokActivity {
  /** Number of shares. */
  share?: number;
  /** Number of likes. */
  like?: number;
  /** Number of comments. */
  comment?: number;
}

export interface FacebookReel {
  /** The page ID to publish the reel to. */
  pageId?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** A description for the reel. */
  description?: string | null;
  /**
   * Insights from Facebook.
   * @readonly
   */
  insights?: FacebookInsights;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface FacebookStory {
  /**
   * The page ID to publish the story to.
   * @maxLength 1024
   */
  pageId?: string | null;
  /**
   * The story URL on Facebook.
   * @readonly
   * @format WEB_URL
   */
  storyUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from Facebook.
   * @readonly
   */
  insights?: FacebookInsights;
}

export interface YouTubeVideo {
  /** The channel ID to publish the video to. */
  channelId?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** A title for the video. */
  title?: string | null;
  /** A description for the video. */
  description?: string | null;
  /** A category for the video. */
  categoryId?: string | null;
  /** Whether to notify channel subscribers when the video is published. */
  notifySubscribers?: boolean;
  /**
   * Insights data from YouTube.
   * @readonly
   */
  insights?: YoutubeInsights;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface YoutubeInsights {
  /** Number of views. */
  views?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of dislikes. */
  dislikes?: number;
  /** Number of shares. */
  shares?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface YouTubeShort {
  /** The channel ID to publish the short to. */
  channelId?: string | null;
  /** The short URL. */
  videoUrl?: string | null;
  /** A title for the short. */
  title?: string | null;
  /** A description for the short. */
  description?: string | null;
  /** A category for the short. */
  categoryId?: string | null;
  /** Whether to notify channel subscribers when the short is published. */
  notifySubscribers?: boolean;
  /**
   * Insights data from YouTube.
   * @readonly
   */
  insights?: YoutubeInsights;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface LinkedinPost extends LinkedinPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /** Author ID of the post. Can be an organization page or a personal feed. */
  authorId?: string | null;
  /** Caption for the post. */
  caption?: string | null;
  /** Link preview metadata. Available only when the post contains a link. */
  linkMetadata?: LinkMetadata;
  /**
   * Insights from LinkedIn.
   * @readonly
   */
  insights?: LinkedinPostInsights;
}

/** @oneof */
export interface LinkedinPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface LinkedinPostInsights {
  /** Number of unique impressions. */
  uniqueImpressions?: number;
  /** Total number of impressions. */
  impression?: number;
  /** Number of shares. */
  shares?: number;
  /** Engagement rate. */
  engagement?: number;
  /** Number of clicks. */
  clicks?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of comments. */
  comments?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

/** Deprecated: No longer functional as of July 31, 2026. */
export interface TwitterPost extends TwitterPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /** Caption for the post. */
  caption?: string | null;
  /** Link preview metadata. Available only when the post contains a link. */
  linkMetadata?: LinkMetadata;
  /**
   * Insights from X (Twitter).
   * @readonly
   */
  insights?: TwitterInsights;
}

/** @oneof */
export interface TwitterPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

/** Deprecated: No longer functional as of July 31, 2026. */
export interface TwitterInsights {
  /** Number of retweets. */
  retweets?: number;
  /** Number of replies. */
  replies?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of quote posts. */
  quotes?: number;
  /** Number of bookmarks. */
  bookmarks?: number;
  /** Number of impressions. */
  impressions?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface PinterestPost {
  /**
   * The board ID to publish the post to.
   * @maxLength 128
   */
  boardId?: string | null;
  /**
   * The title of the post.
   * @maxLength 100
   */
  title?: string | null;
  /**
   * The description of the post.
   * @maxLength 800
   */
  description?: string | null;
  /**
   * The link to include in the post.
   * @maxLength 2048
   */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from Pinterest.
   * @readonly
   */
  insights?: PinterestInsights;
}

export interface PinterestInsights {
  /** Number of impressions. */
  impressions?: number;
  /** Number of clicks. */
  clicks?: number;
  /** Number of saves. */
  saves?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of reactions. */
  reactions?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface GbpPost {
  /**
   * The location ID to publish the post to.
   * @maxLength 128
   */
  locationId?: string | null;
  /**
   * Account (group) ID of the location.
   * @maxLength 128
   */
  locationAccountId?: string | null;
  /**
   * The description of the post.
   * @maxLength 1500
   */
  description?: string | null;
  /** Call to action type and URL to include in the post. */
  callToAction?: GbpPostCallToAction;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from Google Business Profile.
   * @readonly
   */
  insights?: GbpInsights;
}

export interface GbpPostCallToAction {
  /** Type of the call to action. */
  actionType?: GbpPostCallToActionTypeWithLiterals;
  /**
   * URL of the call to action.
   * @maxLength 2048
   */
  url?: string | null;
}

/** Type of call to action button shown on the post. */
export enum GbpPostCallToActionType {
  BOOK = 'BOOK',
  ORDER = 'ORDER',
  SHOP = 'SHOP',
  LEARN_MORE = 'LEARN_MORE',
  SIGN_UP = 'SIGN_UP',
}

/** @enumType */
export type GbpPostCallToActionTypeWithLiterals =
  | GbpPostCallToActionType
  | 'BOOK'
  | 'ORDER'
  | 'SHOP'
  | 'LEARN_MORE'
  | 'SIGN_UP';

export interface GbpInsights {
  /** Number of shares. Google Business Profile post insights aren't currently available, so this returns `-1`. */
  shares?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface TikTokVideo {
  /**
   * The account ID to publish the video to.
   * @maxLength 128
   */
  accountId?: string | null;
  /** Privacy level for the video. */
  privacyLevel?: TikTokPrivacyLevelWithLiterals;
  /**
   * A description for the video.
   * @maxLength 2200
   */
  description?: string | null;
  /** Whether comments are disabled for the video. */
  disableComment?: boolean;
  /** Whether duets are disabled for the video. */
  disableDuet?: boolean;
  /** Whether stitches are disabled for the video. */
  disableStitch?: boolean;
  /** Whether the content is a paid partnership promoting a third-party business. */
  brandContentToggle?: boolean;
  /** Whether the content promotes the creator's own business. */
  brandOrganicToggle?: boolean;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from TikTok.
   * @readonly
   */
  insights?: TikTokInsights;
}

export enum TikTokPrivacyLevel {
  PUBLIC_TO_EVERYONE = 'PUBLIC_TO_EVERYONE',
  FOLLOWER_OF_CREATOR = 'FOLLOWER_OF_CREATOR',
  MUTUAL_FOLLOW_FRIENDS = 'MUTUAL_FOLLOW_FRIENDS',
  SELF_ONLY = 'SELF_ONLY',
}

/** @enumType */
export type TikTokPrivacyLevelWithLiterals =
  | TikTokPrivacyLevel
  | 'PUBLIC_TO_EVERYONE'
  | 'FOLLOWER_OF_CREATOR'
  | 'MUTUAL_FOLLOW_FRIENDS'
  | 'SELF_ONLY';

export interface TikTokInsights {
  /** Number of views. */
  views?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of shares. */
  shares?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface TikTokPhoto {
  /**
   * The account ID to publish the photo to.
   * @maxLength 128
   */
  accountId?: string | null;
  /** Privacy level for the photo. */
  privacyLevel?: TikTokPrivacyLevelWithLiterals;
  /**
   * A title for the photo.
   * @maxLength 90
   */
  title?: string | null;
  /**
   * A description for the photo.
   * @maxLength 4000
   */
  description?: string | null;
  /** Whether comments are disabled for the photo. */
  disableComment?: boolean;
  /** Whether to automatically add music to the photo. */
  autoAddMusic?: boolean;
  /** Whether the content is a paid partnership promoting a third-party business. */
  brandContentToggle?: boolean;
  /** Whether the content promotes the creator's own business. */
  brandOrganicToggle?: boolean;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from TikTok.
   * @readonly
   */
  insights?: TikTokInsights;
}

export interface SiteAnalytics {
  /** Number of site sessions attributed to this post. */
  traffic?: number;
  /** Number of page views attributed to this post. */
  pageView?: number;
  /** Site bounce rate attributed to this post. */
  siteBounceRate?: number;
  /** Revenue attributed to this post. */
  revenue?: number;
  /** Conversion funnel data attributed to this post. */
  conversionFunnel?: ConversionFunnel;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
}

export interface ConversionFunnel {
  /** Number of sessions that viewed the cart. */
  cartViewsSessions?: number;
  /** Number of sessions that reached checkout. */
  checkOutSessions?: number;
  /** Number of sessions that placed a store order. */
  storeOrdersSessions?: number;
  /** Number of confirmed bookings. */
  bookingsConfirmed?: number;
}

export interface InsightsSummary {
  /** Number of unique impressions (reach). */
  impressions?: number;
  /** Number of interactions, such as likes and comments. */
  interactions?: number;
  /** Date and time the insights were last updated. */
  updatedDate?: Date | null;
  /** Total number of impressions. */
  impressionsTotal?: number;
}

export interface DraftInfo {
  /**
   * Date and time the draft was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the draft was updated.
   * @readonly
   */
  updatedDate?: Date | null;
}

export interface PublicationErrorDetails {
  /**
   * Error message shown when publishing fails.
   * @readonly
   * @maxLength 500
   */
  message?: string | null;
}

export interface AssetReference {
  /** Type of the site asset (for example, product or blog post). See `SiteAsset.Type` for the closed set of values. */
  siteAssetRefType?: TypeWithLiterals;
  /**
   * ID of the asset within its provider's data store.
   * @maxLength 256
   */
  id?: string | null;
  /**
   * Display name of the asset (for example, the product title or blog post title), cached at post-creation time.
   * @maxLength 3000
   */
  name?: string;
}

export interface ItemAutomationsWrapper {
  /**
   * Automations on the item.
   * @maxSize 10
   */
  automations?: ItemAutomation[];
}

export interface PublishItemRequest {
  /** Item to publish. */
  item?: Item;
  itemExtras?: ItemExtras;
}

export interface ItemExtras {
  /**
   * Where the user began the flow to open the publisher. For example, `stores.product-list`.
   * @maxLength 100
   */
  userOrigin?: string | null;
}

export interface PublishItemResponse {
  /** Published item. */
  item?: Item;
}

/** Triggered when publishing starts for an item. */
export interface ItemPublishStarted {
  /** Item being published. */
  item?: Item;
}

/** Triggered when an item is successfully published to its social channel. */
export interface ItemPublished {
  /** Published item. */
  item?: Item;
}

/** Triggered when an item fails to publish to its social channel. */
export interface ItemPublishFailed {
  /** Item that failed to publish. See `publicationError` for the reason. */
  item?: Item;
}

/** Triggered when an item is scheduled to be published at a future date. */
export interface ItemPublishScheduled {
  /** Scheduled item. */
  item?: Item;
}

export interface PublishItemByIdRequest {
  /**
   * ID of the item to publish.
   * @format GUID
   */
  id: string | null;
  /** Date and time to publish the item. If omitted, the item is published immediately. */
  scheduledDate?: Date | null;
}

export interface PublishItemByIdResponse {
  /** Published item. */
  item?: Item;
}

export interface CreateDraftItemRequest {
  /** Item to create as a draft. */
  item?: Item;
  itemExtras?: ItemExtras;
}

export interface CreateDraftItemResponse {
  /** Created draft item. */
  item?: Item;
}

/** GetOrCreateWixelBindingByFileRequest */
export interface GetOrCreateWixelBindingByFileRequest {
  /**
   * Publisher draft id that owns the media file
   * @format GUID
   */
  draftId?: string;
  /**
   * Media Manager file id to open in Wixel
   * @maxLength 1000
   */
  fileId?: string;
}

/** GetOrCreateWixelBindingByFileResponse */
export interface GetOrCreateWixelBindingByFileResponse {
  /**
   * Wixel editor URL with the bound asset selected
   * @format WEB_URL
   */
  url?: string;
  /**
   * Wixel binding id
   * @format GUID
   */
  bindingId?: string | null;
  /**
   * Wixel asset id
   * @format GUID
   */
  assetId?: string | null;
  /**
   * Wixel project id
   * @format GUID
   */
  projectId?: string | null;
}

/** PublishDraftRequest */
export interface PublishDraftRequest {
  /**
   * The draft item id to publish
   * @format GUID
   */
  id?: string;
}

/** PublishDraftResponse */
export interface PublishDraftResponse {
  /** The published item */
  item?: Item;
}

/** PublishMultiStoryDraftRequest */
export interface PublishMultiStoryDraftRequest {
  /**
   * ID of the multi-story draft to publish.
   * @format GUID
   */
  id: string;
  /** Scheduling info applied to all story items. If set, the story items are scheduled instead of published immediately. */
  schedulingInfo?: SchedulingInfo;
}

/** PublishMultiStoryDraftResponse */
export interface PublishMultiStoryDraftResponse {
  /** Results, one per story item handled in this call, in story order. Story items already published by a previous call aren't included. */
  results?: BulkItemsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkItemsResult {
  /** Metadata for the individual item's publish result. */
  itemMetadata?: ItemMetadata;
  /** Published item. Returned only when `returnEntity` is `true` in the request. */
  item?: Item;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

/** isEligibleForSchedulingRequest */
export interface IsEligibleForSchedulingRequest {}

/** isEligibleForSchedulingResponse */
export interface IsEligibleForSchedulingResponse {
  isEligible?: boolean;
  reason?: SchedulingRestrictedReasonWithLiterals;
}

export enum SchedulingRestrictedReason {
  NO_SOCIAL_POSTS_SCHEDULING = 'NO_SOCIAL_POSTS_SCHEDULING',
}

/** @enumType */
export type SchedulingRestrictedReasonWithLiterals =
  | SchedulingRestrictedReason
  | 'NO_SOCIAL_POSTS_SCHEDULING';

export interface GetItemRequest {
  /**
   * ID of the item to retrieve.
   * @format GUID
   */
  id: string;
}

export interface GetItemResponse {
  /** Retrieved item. */
  item?: Item;
}

export interface GetItemsRequest {
  /**
   * IDs of the items to retrieve.
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
}

export interface GetItemsResponse {
  /** Retrieved items. */
  items?: Item[];
}

export interface QueryItemsRequest {
  /** Query options, including paging, filtering, and sorting. */
  query: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryItemsResponse {
  /** Retrieved items. */
  items?: Item[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface CancelScheduledItemRequest {
  /**
   * ID of the scheduled item to cancel.
   * @format GUID
   */
  id: string;
}

export interface CancelScheduledItemResponse {
  /** Canceled item. */
  item?: Item;
}

export interface UpdateItemRequest {
  /** Item to update. */
  item: Item;
  /** @internal */
  mask?: string[];
}

export interface UpdateItemResponse {
  /** Updated item. */
  item?: Item;
}

export interface RescheduleItemRequest {
  /**
   * ID of the scheduled item to reschedule.
   * @format GUID
   */
  id: string | null;
  /** Date and time to publish the item. If omitted, the item is published immediately. */
  scheduledDate?: Date | null;
}

export interface RescheduleItemResponse {
  /** Rescheduled item. */
  item?: Item;
}

/** DeleteItemRequest */
export interface DeleteItemRequest {
  /** @format GUID */
  id: string;
}

/** DeleteItemResponse */
export interface DeleteItemResponse {}

export interface BulkPublishItemsRequest {
  /**
   * Items to publish.
   * @minSize 1
   * @maxSize 20
   */
  items: Item[];
  /** Policy that determines how items are published when one of them fails. */
  policy?: BulkPublishItemsPolicyWithLiterals;
  /** Scheduling info applied to all items. If set, the items are scheduled instead of published immediately. */
  schedulingInfo?: SchedulingInfo;
  itemExtras?: ItemExtras;
  /**
   * Whether to return the published items in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/** Policy that determines how items are published when one of them fails. */
export enum BulkPublishItemsPolicy {
  /** Publish all items, regardless of whether individual items fail. */
  CONCURRENT = 'CONCURRENT',
  /** Stop publishing remaining items as soon as one item fails. */
  SEQUENTIAL_STOP_ON_FAIL = 'SEQUENTIAL_STOP_ON_FAIL',
}

/** @enumType */
export type BulkPublishItemsPolicyWithLiterals =
  | BulkPublishItemsPolicy
  | 'CONCURRENT'
  | 'SEQUENTIAL_STOP_ON_FAIL';

export interface BulkPublishItemsResponse {
  /**
   * Results of the bulk publish, one per item, in the order they were sent.
   * @minSize 1
   * @maxSize 20
   */
  results?: BulkItemsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type PublishItemApplicationErrors =
  | {
      code?: 'REFERENCE_ID_ALREADY_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PUBLISH_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type PublishItemByIdApplicationErrors =
  | {
      code?: 'REFERENCE_ID_ALREADY_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PUBLISH_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MULTI_STORY_PUBLISH_NOT_SUPPORTED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type PublishMultiStoryDraftApplicationErrors =
  | {
      code?: 'NOT_MULTI_STORY_DRAFT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CancelScheduledItemApplicationErrors =
  | {
      code?: 'SCHEDULED_ITEM_ALREADY_CANCELED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SCHEDULED_ITEM_ALREADY_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SCHEDULED_ITEM_ALREADY_DELETED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateItemApplicationErrors =
  | {
      code?: 'ITEM_NOT_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SCHEDULED_ITEM_DATE_CHANGED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type RescheduleItemApplicationErrors =
  | {
      code?: 'ITEM_NOT_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_IS_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_IS_DELETED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type DeleteItemApplicationErrors =
  | {
      code?: 'ITEM_IS_DELETED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_NOT_EXISTS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkPublishItemsApplicationErrors = {
  code?: 'INELIGIBLE_FOR_FEATURE';
  description?: string;
  data?: Record<string, any>;
};
